import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getAllServices, getAllPacks } from "../../../helpers/fakebackend_helper";
import "./ApiHubLanding.css";

// Per-call API pricing — visual grouping for the live pricing section.
// Any service_id not listed falls into "Other".
const PRICE_CATEGORIES = [
  { key: "image", label: "Image Generation", color: "var(--accent)", ids: ["TEXT_TO_IMAGE", "IMAGE_TO_IMAGE", "ENHANCE_IMAGE", "REMOVE_BACKGROUND", "REMOVE_OBJECT", "SWAP_OUTFIT", "AI_AVATAR", "SWAP_FACE_IMAGE"] },
  { key: "video", label: "Video Generation", color: "oklch(0.72 0.18 250)", ids: ["IMAGE_TO_VIDEO", "TEXT_TO_VIDEO", "TRANSITION_VIDEO", "LIPSYNC", "LIVEPORTRAIT", "AUDIO_TO_VIDEO", "SWAP_FACE_VIDEO", "PHOTO_DANCE"] },
  { key: "audio", label: "Audio & Music", color: "oklch(0.72 0.18 350)", ids: ["SONG_GENERATE_WITH_PROMPT", "SONG_GENERATE_WITH_LYRICS", "SONG_PROMPT_SUGGEST", "AUDIO_SEPERATE", "COVER_MODEL_UPLOAD", "COVER_MODEL_TRAIN", "COVER_GENERATE", "YOUTUBE_DOWNLOAD"] },
  { key: "identification", label: "Identification", color: "oklch(0.78 0.16 145)", ids: ["IDENTIFY_PLANT", "IDENTIFY_PLANT_DETAIL", "IDENTIFY_PLANT_DETAIL_BY_NAME", "IDENTIFY_PLANT_DISEASE", "IDENTIFY_INSECT", "IDENTIFY_BIRD", "IDENTIFY_MUSHROOM", "IDENTIFY_STONE", "IDENTIFY_COIN", "IDENTIFY_COIN_AI", "IDENTIFY_ANTIQUE"] },
  { key: "utility", label: "Utilities", color: "oklch(0.78 0.15 70)", ids: ["CHECK_NSFW", "CALL_VLLM"] },
];

const priceName = (svc) => {
  if (svc.service_name && svc.service_name.trim()) return svc.service_name;
  return svc.service_id.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const services = {
  image: [
    { name: "Text to Image", desc: "Generate stunning, high-quality images from text prompts.", glyph: "✦", badge: "Demo" },
    { name: "Image to Image", desc: "Restyle and transform existing images with a prompt.", glyph: "↻" },
    { name: "Enhance Image", desc: "Upscale and improve image quality with AI restoration.", glyph: "★" },
    { name: "Remove Background", desc: "Cleanly extract subjects from any photo background.", glyph: "◯" },
    { name: "Remove Object", desc: "Erase unwanted objects from images seamlessly.", glyph: "◫" },
    { name: "Swap Outfit", desc: "Try on different outfits using AI-powered virtual fitting.", glyph: "✧" },
    { name: "AI Avatar", desc: "Create personalized AI avatars from a single photo.", glyph: "☺" },
    { name: "Faceswap Image", desc: "Swap faces between images with realistic precision.", glyph: "◐", badge: "Demo" },
  ],
  video: [
    { name: "Image to Video", desc: "Animate static images into captivating videos.", glyph: "▶" },
    { name: "Transition Video", desc: "Create smooth transition videos between two images.", glyph: "⇄" },
    { name: "Lipsync Video", desc: "Make portraits lipsync to any audio clip you provide.", glyph: "◉" },
    { name: "Live Portrait", desc: "Animate portraits with realistic, lifelike motion.", glyph: "✺", badge: "Demo" },
    { name: "Face Sing", desc: "Sync facial expressions to audio for singing animations.", glyph: "♫", badge: "Demo" },
    { name: "Faceswap Video", desc: "Replace faces in videos frame-by-frame with precision.", glyph: "⊛", badge: "Demo" },
  ],
  audio: [
    { name: "Song from Prompt", desc: "Compose full songs from a single text description.", glyph: "♬" },
    { name: "Song from Lyrics", desc: "Turn your lyrics into a finished song with AI vocals.", glyph: "♪" },
    { name: "Audio Separator", desc: "Split tracks into vocals, drums, bass, and more.", glyph: "≡" },
    { name: "AI Voice Cover", desc: "Generate cover songs with custom AI voice models.", glyph: "◈" },
    { name: "Media Downloader", desc: "Download audio & video from major platforms, watermark-free.", glyph: "⇣" },
  ],
  tools: [
    { name: "AI Assistant", desc: "Smart chat assistant powered by vision-language models.", glyph: "✱" },
    { name: "AI Identification", desc: "Identify plants, birds, insects, mushrooms, stones, coins.", glyph: "◎" },
    { name: "NSFW Detection", desc: "Automatically detect inappropriate content in images.", glyph: "▲" },
  ],
};

const ApiHubLanding = () => {
  const heroRef = useRef(null);
  const rootRef = useRef(null);

  // Live per-call API pricing (from the same endpoint the dashboard uses).
  const [apiServices, setApiServices] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  // Live credit packs (same endpoint as the in-app /plan page).
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);

  useEffect(() => { document.title = "ApiHub — The all-in-one AI API for creators"; }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllServices();
        if (!cancelled && res && res.success) setApiServices(res.data || []);
      } catch (e) { /* ignore — public landing, show nothing on failure */ }
      finally { if (!cancelled) setApiLoading(false); }
    })();
    (async () => {
      try {
        const res = await getAllPacks();
        if (!cancelled && res && res.success) setPacks(res.data || []);
      } catch (e) { /* ignore — public landing, show nothing on failure */ }
      finally { if (!cancelled) setPacksLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const sortedPacks = useMemo(
    () => packs.slice().sort((a, b) => (a.price || 0) - (b.price || 0)),
    [packs]
  );

  const pricingGrouped = useMemo(() => {
    const idToCategory = {};
    PRICE_CATEGORIES.forEach(cat => cat.ids.forEach(id => { idToCategory[id] = cat.key; }));
    const buckets = { other: [] };
    PRICE_CATEGORIES.forEach(cat => { buckets[cat.key] = []; });
    apiServices.forEach(svc => {
      const catKey = idToCategory[svc.service_id] || "other";
      (buckets[catKey] = buckets[catKey] || []).push(svc);
    });
    Object.keys(buckets).forEach(k => buckets[k].sort((a, b) => (a.credit || 0) - (b.credit || 0)));
    return buckets;
  }, [apiServices]);

  // Mouse spotlight on hero
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const handler = (e) => {
      const r = hero.getBoundingClientRect();
      hero.style.setProperty("--mx", ((e.clientX - r.left) / r.width * 100) + "%");
      hero.style.setProperty("--my", ((e.clientY - r.top) / r.height * 100) + "%");
    };
    hero.addEventListener("pointermove", handler);
    return () => hero.removeEventListener("pointermove", handler);
  }, []);

  // Spotlight on service & feature cards
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cards = root.querySelectorAll(".svc, .feature");
    const handlers = [];
    cards.forEach(card => {
      const isFeature = card.classList.contains("feature");
      const handler = (e) => {
        const r = card.getBoundingClientRect();
        card.style.setProperty(isFeature ? "--fx" : "--sx", (e.clientX - r.left) + "px");
        card.style.setProperty(isFeature ? "--fy" : "--sy", (e.clientY - r.top) + "px");
      };
      card.addEventListener("pointermove", handler);
      handlers.push([card, handler]);
    });
    return () => handlers.forEach(([c, h]) => c.removeEventListener("pointermove", h));
  }, []);

  // Scroll reveal + stat counters
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const revealEls = root.querySelectorAll(".section-head, .features, .svc-grid, .stats, .pricing, .testimonial, .final, .cat");
    revealEls.forEach(el => el.classList.add("reveal"));
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); } });
    }, { threshold: 0.12 });
    revealEls.forEach(el => io.observe(el));

    const stats = root.querySelectorAll(".stat-num");
    const animateCount = (el) => {
      const html = el.innerHTML;
      const m = html.match(/^([\d.,]+)/);
      if (!m) return;
      const target = parseFloat(m[1].replace(/,/g, ""));
      if (isNaN(target)) return;
      const decimals = (m[1].split(".")[1] || "").length;
      const rest = html.slice(m[1].length);
      const dur = 1100;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = (target * eased).toFixed(decimals);
        el.innerHTML = val + rest;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const statIo = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { animateCount(en.target); statIo.unobserve(en.target); } });
    }, { threshold: 0.4 });
    stats.forEach(el => statIo.observe(el));

    return () => { io.disconnect(); statIo.disconnect(); };
  }, []);

  // Code tabs (visual only)
  const [codeTab, setCodeTab] = React.useState("faceswap.ts");

  const Cat = ({ barColor = "var(--accent)", name, count, items }) => (
    <div className="cat">
      <div className="cat-head">
        <span className="cat-bar" style={{ background: barColor }}></span>
        <span className="cat-name">{name}</span>
        <span className="cat-count">{count} services</span>
      </div>
      <div className="svc-grid">
        {items.map((s, i) => (
          <div className="svc" key={i}>
            <div className="svc-glyph">{s.glyph}</div>
            <div className="svc-name">{s.name}</div>
            <div className="svc-desc">{s.desc}</div>
            <div className="svc-foot">Available via API</div>
            {s.badge && <div className="svc-badge">{s.badge}</div>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="ah-landing" ref={rootRef}>
      {/* NAV */}
      <header className="nav">
        <div className="container nav-inner">
          <Link to="/" className="brand">
            <span className="api">Api</span><span className="hub">hub</span>
          </Link>
          <nav className="nav-links">
            <a href="#services">Services</a>
            <a href="#features">Features</a>
            <a href="#api-pricing">Pricing</a>
            <a href="#pricing">Plans</a>
            <a href="#reviews">Reviews</a>
          </nav>
          <span className="nav-spacer"></span>
          <div className="nav-right">
            <Link className="btn btn-ghost" to="/login">Sign in</Link>
            <Link className="btn btn-primary" to="/dashboard">Dashboard <span className="arrow">→</span></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="hero" ref={heroRef}>
        <div className="hero-grid"></div>
        <div className="container hero-inner">
          <div className="eyebrow">
            <span className="tag">22+ APIs</span>
            <span>One key for every AI service</span>
          </div>
          <h1 className="h1">The all-in-one<br /><em>AI API for creators.</em></h1>
          <p className="lede">Generate images, animate portraits, swap faces, compose songs, and more — all through a single REST API. Pay-as-you-go credits, no monthly minimum, crypto accepted.</p>
          <div className="hero-cta">
            <Link className="btn btn-accent btn-lg" to="/register">Get started free <span className="arrow">→</span></Link>
            <a className="btn btn-outline btn-lg" href="#services">Explore APIs</a>
          </div>
          <div className="hero-meta">
            <span><span className="check"></span> No credit card</span>
            <span><span className="check"></span> Free playground</span>
            <span><span className="check"></span> Crypto payments</span>
          </div>

          {/* Code panel */}
          <div className="code-wrap">
            <div className="code-card">
              <div className="code-tabs">
                <div className="code-dots"><i></i><i></i><i></i></div>
                {["faceswap.ts", "curl", "python"].map(t => (
                  <div key={t} className={"code-tab" + (codeTab === t ? " active" : "")} onClick={() => setCodeTab(t)}>{t}</div>
                ))}
                <div className="code-spacer"></div>
                <div className="code-action">▶ run</div>
              </div>
              <div className="code-body">
                <div className="code-pane"><span className="tok-com">{"// One key, every service"}</span>{"\n"}
<span className="tok-kw">import</span>{" { "}<span className="tok-fn">ApiHub</span>{" } "}<span className="tok-kw">from</span>{" "}<span className="tok-str">"apihub"</span>;{"\n\n"}
<span className="tok-kw">const</span>{" hub = "}<span className="tok-kw">new</span>{" "}<span className="tok-fn">ApiHub</span>({"({"}{"\n"}
{"  apiKey: process.env."}<span className="tok-key">APIHUB_KEY</span>,{"\n"}
{"});"}{"\n\n"}
<span className="tok-kw">const</span>{" result = "}<span className="tok-kw">await</span>{" hub.image."}<span className="tok-fn">faceswap</span>({"({"}{"\n"}
{"  source: "}<span className="tok-str">"./tony.png"</span>,{"\n"}
{"  target: "}<span className="tok-str">"./team-photo.jpg"</span>,{"\n"}
{"  quality: "}<span className="tok-str">"high"</span>,{"\n"}
{"});"}{"\n\n"}
<span className="tok-com">{"// → result.url, result.credits_used"}</span>{"\n"}
{"console."}<span className="tok-fn">log</span>{"(result.url);"}</div>
                <div className="response-pane">
                  <div className="resp-head">
                    <span className="pill">200 OK</span>
                    <span>image/faceswap</span>
                    <span>·</span>
                    <span>3.4s · 12 credits</span>
                  </div>
                  <div style={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-elev-2)", padding: 16, marginBottom: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                      <div style={{ aspectRatio: "1", borderRadius: 6, background: "repeating-linear-gradient(135deg,oklch(0.4 0.05 30) 0 6px,oklch(0.35 0.04 30) 6px 12px)", display: "grid", placeItems: "center", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)" }}>SRC</div>
                      <div style={{ aspectRatio: "1", borderRadius: 6, background: "repeating-linear-gradient(135deg,oklch(0.4 0.05 240) 0 6px,oklch(0.35 0.04 240) 6px 12px)", display: "grid", placeItems: "center", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)" }}>TGT</div>
                      <div style={{ aspectRatio: "1", borderRadius: 6, background: "linear-gradient(135deg,var(--accent-soft),color-mix(in oklab, var(--accent) 30%, transparent))", display: "grid", placeItems: "center", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--accent)", fontWeight: 600 }}>OUT</div>
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-muted)" }}>
                    <span style={{ color: "var(--fg-dim)" }}>url:</span> <span style={{ color: "var(--accent)" }}>cdn.apihub.today/r/8f2a…</span><br />
                    <span style={{ color: "var(--fg-dim)" }}>credits:</span> 12<span className="cursor"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="section" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div className="container">
          <div className="stats">
            <div className="stat">
              <div className="stat-num">22<span className="unit">+</span></div>
              <div className="stat-label">AI services, one API</div>
            </div>
            <div className="stat">
              <div className="stat-num">4</div>
              <div className="stat-label">Categories: image, video, audio, tools</div>
            </div>
            <div className="stat">
              <div className="stat-num">$0<span className="unit"> min</span></div>
              <div className="stat-label">Pay-as-you-go, no monthly fees</div>
            </div>
            <div className="stat">
              <div className="stat-num">3<span className="unit"> ways</span></div>
              <div className="stat-label">Pay with card, crypto, or wallet</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES CATALOG */}
      <section className="section" id="services" style={{ paddingTop: 0, borderTop: "none" }}>
        <div className="container">
          <div className="section-head" style={{ textAlign: "center", margin: "0 auto 56px" }}>
            <div className="section-eyebrow" style={{ display: "block" }}>22+ AI Services</div>
            <h2 className="section-title">Everything you need,<br />in <em style={{ fontStyle: "normal", color: "var(--accent)" }}>one API</em>.</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>From image generation to lipsync, song creation, and AI assistants — call any of 22+ services with a single API key.</p>
          </div>

          <Cat name="Image" count={services.image.length} items={services.image} />
          <Cat name="Video" count={services.video.length} items={services.video} barColor="oklch(0.72 0.18 250)" />
          <Cat name="Audio & Music" count={services.audio.length} items={services.audio} barColor="oklch(0.72 0.18 350)" />
          <Cat name="AI Tools" count={services.tools.length} items={services.tools} barColor="oklch(0.78 0.16 145)" />

          <div style={{ marginTop: 48, textAlign: "center", display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Link className="btn btn-accent" to="/dashboard">Try in playground <span className="arrow">→</span></Link>
            <a className="btn btn-outline" href="#pricing">See pricing</a>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" id="features">
        <div className="container">
          <div className="section-head">
            <div className="section-eyebrow">Features</div>
            <h2 className="section-title">Discover what<br />sets us apart.</h2>
            <p className="section-sub">Built for creators who want to ship — not stitch together five different vendors.</p>
          </div>
          <div className="features">
            <div className="feature">
              <div className="feature-glyph">★</div>
              <div className="feature-title">High-Quality Outputs</div>
              <div className="feature-desc">Enjoy superior quality across every AI-generated asset — image, video, and audio.</div>
              <div className="feature-foot">SOTA models · curated providers</div>
            </div>
            <div className="feature">
              <div className="feature-glyph">⚙</div>
              <div className="feature-title">User-Friendly API</div>
              <div className="feature-desc">Seamlessly integrate our APIs into your projects with one REST endpoint and one key.</div>
              <div className="feature-foot">REST · webhooks · SDKs</div>
            </div>
            <div className="feature">
              <div className="feature-glyph">⚡</div>
              <div className="feature-title">Fast Processing</div>
              <div className="feature-desc">Experience rapid generation times for all your creative needs, parallelized across GPUs.</div>
              <div className="feature-foot">Sub-second to seconds · async jobs</div>
            </div>
            <div className="feature">
              <div className="feature-glyph">∞</div>
              <div className="feature-title">Scalable Solutions</div>
              <div className="feature-desc">Perfect for individual developers and large enterprises alike — same key, any volume.</div>
              <div className="feature-foot">Auto-scaling · enterprise SLAs</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="section" id="reviews">
        <div className="container">
          <div className="testimonial">
            <div className="testi-mark">"</div>
            <p className="testi-quote">Their API is incredibly easy to use and the results are stunning. We replaced four vendors with ApiHub in a weekend.</p>
            <div className="testi-who"><strong>Mark T.</strong> · Indie creator &amp; ApiHub user</div>
          </div>
        </div>
      </section>

      {/* API PRICING — live per-call credit cost */}
      <section className="section" id="api-pricing">
        <div className="container">
          <div className="section-head" style={{ textAlign: "center", margin: "0 auto 56px" }}>
            <div className="section-eyebrow" style={{ display: "block" }}>API Pricing</div>
            <h2 className="section-title">Transparent cost<br />for <em style={{ fontStyle: "normal", color: "var(--accent)" }}>every call</em>.</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>Pay only for what you generate — each API has a fixed credit cost. No subscription required, and credits never expire.</p>
          </div>

          {apiLoading ? (
            <div className="apx-note">Loading live pricing…</div>
          ) : apiServices.length === 0 ? (
            <div className="apx-note">Pricing is temporarily unavailable. <Link to="/pricing" style={{ color: "var(--accent)" }}>View full pricing →</Link></div>
          ) : (
            <div className="apx-cats">
              {[...PRICE_CATEGORIES, { key: "other", label: "Other", color: "var(--fg-dim)" }].map(cat => {
                const items = pricingGrouped[cat.key] || [];
                if (!items.length) return null;
                return (
                  <div className="apx-cat" key={cat.key}>
                    <div className="apx-cat-head">
                      <span className="apx-cat-dot" style={{ background: cat.color }}></span>
                      <span className="apx-cat-name">{cat.label}</span>
                      <span className="apx-cat-count">{items.length} {items.length === 1 ? "API" : "APIs"}</span>
                    </div>
                    <div className="apx-grid">
                      {items.map(svc => (
                        <div className="apx-item" key={svc.service_id}>
                          <span className="apx-item-name">{priceName(svc)}</span>
                          <span className="apx-item-credits">{svc.credit} {svc.credit === 1 ? "credit" : "credits"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 44, textAlign: "center", display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Link className="btn btn-accent" to="/pricing">See full pricing <span className="arrow">→</span></Link>
            <a className="btn btn-outline" href="#pricing">View credit packs</a>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing">
        <div className="container">
          <div className="section-head" style={{ textAlign: "center", margin: "0 auto 56px" }}>
            <div className="section-eyebrow" style={{ display: "block" }}>Plans</div>
            <h2 className="section-title">Pay only for what<br />you generate.</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>Credits never expire. Top up with card, crypto, or wallet. Bigger packs unlock bonus credits.</p>
          </div>

          {packsLoading ? (
            <div className="apx-note">Loading plans…</div>
          ) : sortedPacks.length === 0 ? (
            <div className="apx-note">Plans are temporarily unavailable. <Link to="/plan" style={{ color: "var(--accent)" }}>View plans →</Link></div>
          ) : (
            <div className="pricing">
              {sortedPacks.map((pack, idx, arr) => {
                // Same bonus math as the in-app /plan page: the second cheapest pack is
                // the baseline rate; bigger packs show how much extra credit they give.
                const baseIdx = Math.min(1, arr.length - 1);
                const baseRate = arr[baseIdx].price > 0 ? arr[baseIdx].credit / arr[baseIdx].price : 0;
                const thisRate = pack.price > 0 ? pack.credit / pack.price : 0;
                const bonusPct = baseRate > 0 ? Math.round(((thisRate / baseRate) - 1) * 100) : 0;
                const featured = !!pack.is_popular;
                return (
                  <div className={"tier" + (featured ? " featured" : "")} key={pack.id}>
                    <div className="tier-name">{pack.name} {featured && <span className="tier-tag">popular</span>}</div>
                    <div className="tier-price">${(pack.price || 0).toLocaleString("en-US")}</div>
                    <div className="tier-sub">{(pack.credit || 0).toLocaleString("en-US")} credits{bonusPct > 0 ? ` · ${bonusPct}% bonus` : ""}</div>
                    <ul className="tier-list">
                      {pack.description && pack.description.trim() && <li>{pack.description}</li>}
                      <li>Access to all AI services</li>
                      <li>Card &amp; crypto payments</li>
                      <li>Credits never expire</li>
                      <li>Free playground access</li>
                    </ul>
                    <div className="tier-cta">
                      <Link className={featured ? "btn btn-accent" : "btn btn-outline"} to="/plan">
                        {featured ? <>Top up &amp; save <span className="arrow">→</span></> : "Top up"}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section" style={{ borderTop: "none" }}>
        <div className="container">
          <div className="final">
            <div className="final-inner">
              <h2>One key. Every AI<br />service. Zero setup.</h2>
              <p>Generate your first image, video, or song in under 60 seconds. No credit card required.</p>
              <div className="hero-cta">
                <Link className="btn btn-accent btn-lg" to="/register">Get started free <span className="arrow">→</span></Link>
                <Link className="btn btn-outline btn-lg" to="/dashboard">Open playground</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div>
              <Link to="/" className="brand" style={{ marginBottom: 14 }}>
                <span className="api">Api</span><span className="hub">hub</span>
              </Link>
              <p style={{ color: "var(--fg-muted)", fontSize: 13, maxWidth: 280, marginTop: 12 }}>
                The all-in-one AI API platform for creators. Have questions?{" "}
                <a href="mailto:support@apihub.today" style={{ color: "var(--accent)" }}>support@apihub.today</a>
              </p>
            </div>
            <div>
              <h5>Services</h5>
              <ul>
                <li><a href="#services">Image APIs</a></li>
                <li><a href="#services">Video APIs</a></li>
                <li><a href="#services">Audio &amp; Music</a></li>
                <li><a href="#services">AI Tools</a></li>
              </ul>
            </div>
            <div>
              <h5>Developers</h5>
              <ul>
                <li><a href="#">Documentation</a></li>
                <li><Link to="/dashboard">Playground</Link></li>
                <li><a href="#">Changelog</a></li>
                <li><a href="#">Status</a></li>
              </ul>
            </div>
            <div>
              <h5>Company</h5>
              <ul>
                <li><a href="#">About</a></li>
                <li><a href="#reviews">Reviews</a></li>
                <li><a href="#">Telegram</a></li>
                <li><a href="mailto:support@apihub.today">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5>Legal</h5>
              <ul>
                <li><a href="#">Terms</a></li>
                <li><a href="#">Privacy</a></li>
                <li><a href="#">Refunds</a></li>
                <li><a href="#">Acceptable use</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <div>© {new Date().getFullYear()} ApiHub</div>
            <div className="status"><span className="dot"></span> All systems normal · pay-as-you-go credits</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApiHubLanding;
